// ============================================================================
// StopCard — one stop on the AM Today's Route view. Big tap targets, one-
// handed use. Shows RAG dot, cadence/parity chips, note, and per-stop actions
// (Visit · Skip · reorder). Skipped/Cancelled stops render struck + grayed;
// Visited stops show a check. Ad-hoc stops carry a tag.
// ============================================================================

import {
  Check,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  SkipForward,
  Sparkles,
} from 'lucide-react';
import { RagDot } from '../../../shared/components';
import { ServiceStateChip } from '../../account-management/stop-service-parts';
import {
  CADENCE_LABELS,
  type ResolvedStop,
} from '../hooks/use-am-routes-data';

export interface StopCardProps {
  stop: ResolvedStop;
  index: number;
  total: number;
  busy?: boolean;
  onVisit: () => void;
  onSkip: () => void;
  onReset: () => void;
  onMove: (dir: 'up' | 'down') => void;
  /** Open the facility's account page (AFB-0063 R-1). Absent for
   *  non-facility stops — the row is then not a tap target. */
  onOpen?: () => void;
}

export default function StopCard({
  stop,
  index,
  total,
  busy,
  onVisit,
  onSkip,
  onReset,
  onMove,
  onOpen,
}: StopCardProps) {
  const inactive = stop.status === 'Skipped' || stop.status === 'Cancelled';
  const visited = stop.status === 'Visited';

  return (
    <div
      // Whole card is the tap target (R-1); controls stopPropagation so a
      // Visit/Skip/reorder tap never navigates, and a row tap never marks.
      onClick={onOpen}
      className={`rounded-xl border bg-tcs-raised p-3 shadow-sm transition-colors ${
        onOpen ? 'cursor-pointer active:bg-tcs-bg-muted/40' : ''
      } ${
        inactive
          ? 'border-tcs-border bg-tcs-bg-muted/60'
          : visited
            ? 'border-st-ok-border bg-st-ok-bg/40'
            : 'border-tcs-border'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Reorder rail */}
        <div className="flex flex-col items-center pt-0.5">
          <button
            type="button"
            aria-label="Move up"
            disabled={index === 0 || busy}
            onClick={(e) => {
              e.stopPropagation();
              onMove('up');
            }}
            className="rounded p-0.5 text-tcs-text-muted hover:text-tcs-text disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <span className="text-[11px] font-semibold text-tcs-text-muted">
            {index + 1}
          </span>
          <button
            type="button"
            aria-label="Move down"
            disabled={index === total - 1 || busy}
            onClick={(e) => {
              e.stopPropagation();
              onMove('down');
            }}
            className="rounded p-0.5 text-tcs-text-muted hover:text-tcs-text disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <RagDot status={stop.rag} size="lg" />
            <span
              className={`truncate text-base font-medium ${
                inactive
                  ? 'text-tcs-text-muted line-through'
                  : 'text-tcs-text'
              }`}
              {...(onOpen
                ? {
                    role: 'link' as const,
                    tabIndex: 0,
                    'aria-label': `Open ${stop.label}`,
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen();
                      }
                    },
                  }
                : {})}
            >
              {stop.label}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {stop.cadence && (
              <Chip>
                {CADENCE_LABELS[stop.cadence]}
                {stop.cadence === 'Biweekly' &&
                  stop.weekParity &&
                  stop.weekParity !== 'A_B' &&
                  ` · ${stop.weekParity}`}
              </Chip>
            )}
            {/* A paused facility STAYS on the route and is badged — Ben's
                ruling. Without this the crew turns up at a site we agreed to
                stop cleaning, which is the whole reason the badge exists.
                Renders nothing when service is Active. */}
            <ServiceStateChip state={stop.serviceState} size="sm" />
            {stop.isAdHoc && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                <Sparkles className="h-3 w-3" /> Ad-hoc
              </span>
            )}
            {visited && (
              <span className="inline-flex items-center gap-1 rounded-full bg-st-ok-bg-strong px-2 py-0.5 text-[11px] font-semibold text-st-ok-fg">
                <Check className="h-3 w-3" /> Visited
              </span>
            )}
            {inactive && (
              <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] font-semibold text-tcs-text-secondary">
                {stop.status}
              </span>
            )}
          </div>

          {stop.note && !inactive && (
            <p className="mt-1.5 text-xs text-tcs-text-secondary">{stop.note}</p>
          )}
          {inactive && stop.cancelReason && (
            <p className="mt-1.5 text-xs text-tcs-text-muted">
              Reason: {stop.cancelReason}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-2.5 flex items-center gap-2 border-t border-tcs-border/70 pt-2.5">
        {!inactive && !visited && (
          <>
            <ActionButton onClick={onVisit} disabled={busy} tone="primary">
              <Check className="h-4 w-4" /> Visited
            </ActionButton>
            <ActionButton onClick={onSkip} disabled={busy} tone="muted">
              <SkipForward className="h-4 w-4" /> Skip
            </ActionButton>
          </>
        )}
        {(inactive || visited) && (
          <ActionButton onClick={onReset} disabled={busy} tone="muted">
            <RotateCcw className="h-4 w-4" /> Undo
          </ActionButton>
        )}
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone: 'primary' | 'muted';
}) {
  return (
    <button
      type="button"
      // Controls never navigate — the parent card is a tap target (R-1).
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className={`inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        tone === 'primary'
          ? 'bg-tcs-inverse text-tcs-inverse-ink hover:bg-tcs-inverse-hover'
          : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-bg-muted'
      }`}
    >
      {children}
    </button>
  );
}
